export const WordCompletionStatus = {
  UPCOMING: "upcoming",
  CURRENT: "current",
  LISTENING: "listening",
  BUZZED_IN: "buzzedIn",
  CORRECT: "correct",
  INCORRECT: "incorrect",
};

export type WordCompletionStatusType =
  | typeof WordCompletionStatus.UPCOMING
  | typeof WordCompletionStatus.CURRENT
  | typeof WordCompletionStatus.LISTENING
  | typeof WordCompletionStatus.BUZZED_IN
  | typeof WordCompletionStatus.CORRECT
  | typeof WordCompletionStatus.INCORRECT;

export interface BuzzInState {
  isOnIntro: boolean;
  completedWordIndex: number;
  completedWordStatuses: Array<WordCompletionStatusType>;
  timeRemaining: number;
}

export const INITIAL_STATE: BuzzInState = {
  isOnIntro: true,
  completedWordIndex: 0,
  completedWordStatuses: [],
  timeRemaining: 1,
};

export const BuzzInAction = {
  INTRO_CLOSED: "INTRO_CLOSED",
  PRESSED_LISTEN: "PRESSED_LISTEN",
  TIMER_UPDATED: "TIMER_UPDATED",
  TIMER_EXPIRED: "TIMER_EXPIRED",
  BUZZED_IN: "BUZZED_IN",
  ANSWERED_CORRECTLY: "ANSWERED_CORRECTLY",
  ANSWERED_INCORRECTLY: "ANSWERED_INCORRECTLY",
  ROUND_COMPLETED: "ROUND_COMPLETED",
} as const;

export type BuzzInAction =
  | { type: typeof BuzzInAction.INTRO_CLOSED }
  | { type: typeof BuzzInAction.PRESSED_LISTEN; payload: { wordIndex: number } }
  | { type: typeof BuzzInAction.TIMER_UPDATED; payload: { newValue: number } }
  | { type: typeof BuzzInAction.TIMER_EXPIRED }
  | { type: typeof BuzzInAction.BUZZED_IN; payload: { wordIndex: number } }
  | { type: typeof BuzzInAction.ANSWERED_CORRECTLY }
  | { type: typeof BuzzInAction.ROUND_COMPLETED }
  | { type: typeof BuzzInAction.ANSWERED_INCORRECTLY };

export function buzzInReducer(state: BuzzInState, action: BuzzInAction) {
  switch (action.type) {
    case BuzzInAction.INTRO_CLOSED:
      return {
        ...state,
        isOnIntro: false,
      }
    case BuzzInAction.PRESSED_LISTEN:
      return {
        ...state,
        completedWordStatuses: [
          ...state.completedWordStatuses.slice(0, state.completedWordIndex),
          WordCompletionStatus.LISTENING,
          ...state.completedWordStatuses.slice(state.completedWordIndex + 1),
        ],
      };
    case BuzzInAction.TIMER_UPDATED:
      return {
        ...state,
        timeRemaining: action.payload.newValue,
      };
    case BuzzInAction.TIMER_EXPIRED:
      // Same as answering incorrectly
      return {
        ...state,
        timeRemaining: 0,
        completedWordStatuses: [
          ...state.completedWordStatuses.slice(0, state.completedWordIndex),
          WordCompletionStatus.BUZZED_IN,
          ...state.completedWordStatuses.slice(state.completedWordIndex + 1),
        ],
      };
    case BuzzInAction.BUZZED_IN:
      return {
        ...state,
        completedWordStatuses: [
          ...state.completedWordStatuses.slice(0, state.completedWordIndex),
          WordCompletionStatus.BUZZED_IN,
          ...state.completedWordStatuses.slice(state.completedWordIndex + 1),
        ],
      };
    case BuzzInAction.ANSWERED_CORRECTLY:
      return {
        ...state,
        completedWordIndex: state.completedWordIndex + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses.slice(0, state.completedWordIndex),
          WordCompletionStatus.CORRECT,
          ...state.completedWordStatuses.slice(state.completedWordIndex + 1),
        ],
      };
    case BuzzInAction.ANSWERED_INCORRECTLY:
      return {
        ...state,
        completedWordIndex: state.completedWordIndex + 1,
        completedWordStatuses: [
          ...state.completedWordStatuses.slice(0, state.completedWordIndex),
          WordCompletionStatus.INCORRECT,
          ...state.completedWordStatuses.slice(state.completedWordIndex + 1),
        ],
      };
    case BuzzInAction.ROUND_COMPLETED:
      return INITIAL_STATE;
    default:
      return state;
  }
}
